import { useEffect } from 'react';
import { OrderConfirmationPopupProps } from '@helpers/Models';
import { fileUploadConfig } from '@helpers/Constants';

export const OrderConfirmationPopup: React.FC<OrderConfirmationPopupProps> = ({
  orderNumber,
  visible,
  onClose,
}) => {
  /**
   * Effect hook to automatically close the modal after 3 seconds when visible.
   *
   * This effect watches the `visible` and `onClose` props. When `visible` is true,
   * it sets a timer that calls the `onClose` callback after 3000 milliseconds.
   * It also cleans up the timer if the component unmounts or `visible` changes.
   *
   * @param {boolean} visible - Controls whether the modal is shown.
   * @param {() => void} onClose - Callback function to close the modal.
   *
   * @returns {void} Returns nothing; only sets up side effects.
   */
  useEffect(() => {
    if (!visible) return;

    const timer = setTimeout(() => {
      onClose();
    }, fileUploadConfig.NETWORK_DELAY_MS);

    return () => clearTimeout(timer);
  }, [visible, onClose]);

  return (
    <div className="fixed flex inset-0 items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="flex h-[488px] w-[480px] bg-white rounded-xl pb-14 items-center justify-center">
        {/* Main Content */}
        <div className="overflow-auto relative flex flex-col h-full">
          <div className="flex-1 flex items-center justify-center py-8 gap-4">
            <div className="w-full max-w-md flex flex-col justify-start items-center gap-3 px-4">
              <img
                src="https://wildgamebutcher-dev.s3.us-east-1.amazonaws.com/frontend-assets/confirmation_success.png"
                alt="Success"
                className="w-32 h-32 object-contain"
              />
              <h2 className="text-center text-[#27214D] text-3xl font-bold font-manrope leading-normal">
                Thank You
              </h2>
              <div className="flex flex-col justify-center items-center gap-2.5 w-full">
                <h5 className="text-[#808080] font-normal text-center">
                  Your order has been confirmed.
                </h5>
                <button
                  type="button"
                  className="w-full rounded-full px-6 py-2 text-white bg-[#38B83F]"
                >
                  Order Number: {orderNumber}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
